import * as React from 'react';
import { jobsApi } from '../services/jobsApi';
import type { JobRunDetail, JobRunStatus } from '../types/jobs';

const ACTIVE_STATUSES = new Set<JobRunStatus>(['pending', 'running']);
const POLL_INTERVAL_MS = 5_000;

/**
 * Fetches a single job run detail and polls for updates while the run is active.
 *
 * Polling starts when the run status is 'pending' or 'running' and stops
 * automatically on transition to any terminal state.
 * The interval is cleared on unmount and on runUuid change.
 */
export function useJobRun(projectUuid: string, runUuid: string | null) {
  const [data, setData] = React.useState<JobRunDetail | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const isActive = data ? ACTIVE_STATUSES.has(data.status) : false;

  const load = React.useCallback(
    async (quiet = false) => {
      if (!runUuid) return;
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const result = await jobsApi.getJobRunDetail(projectUuid, runUuid);
        setData(result);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [projectUuid, runUuid]
  );

  // Initial load whenever runUuid changes
  React.useEffect(() => {
    if (!runUuid) {
      setData(null);
      return;
    }
    void load();
  }, [runUuid, load]);

  // Poll while active; stop when terminal state is observed
  React.useEffect(() => {
    if (isActive && runUuid) {
      intervalRef.current = setInterval(() => void load(true), POLL_INTERVAL_MS);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, runUuid, load]);

  return { data, loading, error };
}
